import { EarningsPeriod } from '@api/api/earnings';
import { WithNAFallback } from '@design/components/table';
import { Formats, formatDateToLocale } from '@helpers/utils/dates/dates';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import { differenceInDays } from 'date-fns';
import { useTranslation } from 'react-i18next';

import styles from './Earnings.module.scss';
import { formatNumberWithUnit } from './utils/utils';

type EarningsProps = { latest?: EarningsPeriod };

export const UnreportedEarnings = ({ latest }: EarningsProps) => {
  const { t } = useTranslation('widget');
  const languageSetting = useLanguageStore(state => state.languageSetting);
  const latestReportingDate = latest?.reporting_date
    ? new Date(latest.reporting_date)
    : new Date();
  const daysLeftToReport = differenceInDays(latestReportingDate, new Date());

  if (!latest || latest.reported || daysLeftToReport > 14) {
    return null;
  }

  return (
    <section className={styles.upcomingRoot}>
      <p className={styles.upcomingItem}>
        <span className={styles.upcomingTitle}>
          {t('widget:earnings.nextReport')}
        </span>
        <span className={styles.upcomingValue}>
          {formatDateToLocale(
            languageSetting,
            latestReportingDate,
            Formats['PP']
          )}
        </span>
      </p>
      <p className={styles.upcomingItem}>
        <span className={styles.upcomingTitle}>
          {t('widget:earnings.periodEnding')}
        </span>
        <span
          className={styles.upcomingValue}
        >{`${latest.fiscal_period} ${latest.fiscal_year}`}</span>
      </p>
      <p className={styles.upcomingItem}>
        <span className={styles.upcomingTitle}>
          {t('widget:earnings.epsExpectations')}
        </span>
        <span className={styles.upcomingValue}>
          <WithNAFallback
            check={latest.eps.estimate !== undefined}
            value={latest.eps?.estimate?.toFixed(2)}
          />
        </span>
      </p>
      <p className={styles.upcomingItem}>
        <span className={styles.upcomingTitle}>
          {t('widget:earnings.revenueExpectations')}
        </span>
        <span className={styles.upcomingValue}>
          <WithNAFallback
            check={latest.revenue.estimate !== undefined}
            value={formatNumberWithUnit(latest.revenue.estimate)}
          />
        </span>
      </p>
    </section>
  );
};
